import { useEffect, useState, useCallback } from "react";
import { Users, Eye, ThumbsUp, MessageCircle, MailCheck, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { PLATFORM_META, fmt } from "./platformMeta";
import { PlatformCards } from "./PlatformCards";
import { Composer } from "./Composer";
import { CommentsInbox } from "./CommentsInbox";
import { AyrshareSettings } from "./AyrshareSettings";

export function SocialModule({ onDataChange }) {
  const [accounts, setAccounts] = useState([]);
  const [overview, setOverview] = useState(null);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [summaryState, setSummaryState] = useState({ busy: false, result: null });

  const refresh = useCallback(async () => {
    try {
      const [a, o, p, c, s] = await Promise.all([
        api.get("/social/accounts"),
        api.get("/social/overview"),
        api.get("/social/posts"),
        api.get("/social/comments"),
        api.get("/social/settings"),
      ]);
      setAccounts(a.data || []);
      setOverview(o.data || null);
      setPosts(p.data || []);
      setComments(c.data || []);
      setSettings(s.data || null);
      onDataChange?.(o.data);
    } catch (e) { console.warn("Social module: failed to refresh data", e); }
  }, [onDataChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const connect = async (platform, username, password) => {
    try { await api.post(`/social/accounts/${platform}/connect`, { username, password }); await refresh(); return { ok: true }; }
    catch (e) { alert(formatApiError(e)); return { ok: false }; }
  };

  const disconnect = async (platform) => {
    try { await api.post(`/social/accounts/${platform}/disconnect`); await refresh(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const post = async (content, platforms, photo_ids = []) => {
    try { await api.post("/social/posts", { content, platforms, photo_ids }); await refresh(); return { ok: true }; }
    catch (e) { return { ok: false, error: formatApiError(e) }; }
  };

  const replyComments = async (comment_ids, reply) => {
    try { await api.post("/social/comments/reply", { comment_ids, reply }); await refresh(); return { ok: true }; }
    catch (e) { return { ok: false, error: formatApiError(e) }; }
  };

  const sendSummaryNow = async () => {
    setSummaryState({ busy: true, result: null });
    try {
      const { data } = await api.post("/social/summary/send-now");
      setSummaryState({ busy: false, result: data });
    } catch (e) {
      setSummaryState({ busy: false, result: { sent: false, preview: formatApiError(e) } });
    }
  };

  const totals = overview?.totals;

  return (
    <div className="space-y-6" data-testid="social-module">
      {/* Ayrshare live/demo settings */}
      <AyrshareSettings settings={settings} onChanged={refresh} />

      {/* Mode banner */}
      {settings?.live ? (
        <div className="bg-green-50 border-l-4 border-green-600 px-4 py-3 text-sm text-[#23282B]" data-testid="social-live-banner">
          <span className="font-bold uppercase tracking-wider text-green-700 mr-2">Live</span>
          Posts from the composer publish to the real accounts linked in your Ayrshare dashboard. Metrics and comments below are still simulated until analytics wiring is added.
        </div>
      ) : (
        <div className="bg-[#9CC4DB]/15 border-l-4 border-[#9CC4DB] px-4 py-3 text-sm text-[#23282B]" data-testid="social-demo-banner">
          <span className="font-bold uppercase tracking-wider text-[#2F5F80] mr-2">Demo Mode</span>
          Accounts, metrics and comments are simulated so you can review the full workflow. Connect Ayrshare above to go live.
        </div>
      )}

      {/* Platforms */}
      <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-8">
        <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">Linked Platforms</div>
        <p className="mt-1 text-sm text-[#23282B]/60 mb-5">{settings?.live ? "In live mode, accounts are linked in your Ayrshare dashboard — the tiles below are for the demo workflow." : "Click a platform to link the company account."}</p>
        <PlatformCards accounts={accounts} onConnect={connect} onDisconnect={disconnect} />
      </section>

      {/* Metrics overview */}
      {totals && overview.platforms.length > 0 && (
        <section className="bg-[#23282B] text-white p-6 md:p-8" data-testid="social-metrics">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="font-display text-2xl md:text-3xl uppercase">Social Metrics</div>
            <button onClick={sendSummaryNow} disabled={summaryState.busy} data-testid="social-send-summary" className="inline-flex items-center gap-2 border-2 border-[#9CC4DB] text-[#9CC4DB] hover:bg-[#9CC4DB] hover:text-[#23282B] text-xs font-bold tracking-[0.15em] uppercase px-4 py-2.5 transition-colors disabled:opacity-50">
              {summaryState.busy ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />} Send Daily Summary Now
            </button>
          </div>
          <p className="mt-1 text-xs text-white/50">A summary email goes out automatically every day at 7:00 AM Pacific to char@amazingviewcontracting.com.</p>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Users, label: "Followers", value: totals.followers },
              { icon: Eye, label: "Views", value: totals.views },
              { icon: ThumbsUp, label: "Likes", value: totals.likes },
              { icon: MessageCircle, label: `Comments (${totals.unreplied} unreplied)`, value: totals.comments },
            ].map((s) => (
              <div key={s.label} className="border-l-2 border-[#9CC4DB] pl-4" data-testid={`social-stat-${s.label.split(" ")[0].toLowerCase()}`}>
                <s.icon size={16} className="text-[#9CC4DB]" />
                <div className="font-display text-3xl mt-1 leading-none">{fmt(s.value)}</div>
                <div className="mt-1 text-[11px] tracking-[0.14em] uppercase text-white/60">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm" data-testid="social-metrics-table">
              <thead>
                <tr className="text-left text-[11px] tracking-[0.18em] uppercase text-white/50 border-b border-white/15">
                  <th className="py-2 pr-4">Platform</th>
                  <th className="py-2 pr-4">Followers</th>
                  <th className="py-2 pr-4">Views</th>
                  <th className="py-2 pr-4">Likes</th>
                  <th className="py-2 pr-4">Comments</th>
                  <th className="py-2">New (24h)</th>
                </tr>
              </thead>
              <tbody>
                {overview.platforms.map((p) => (
                  <tr key={p.platform} className="border-b border-white/8" data-testid={`social-metrics-row-${p.platform}`}>
                    <td className="py-2.5 pr-4">
                      <span className="inline-flex items-center gap-2">
                        <i className={PLATFORM_META[p.platform].icon} style={{ color: "#9CC4DB" }} /> {p.name}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 font-mono-av">{fmt(p.followers)}</td>
                    <td className="py-2.5 pr-4 font-mono-av">{fmt(p.views)}</td>
                    <td className="py-2.5 pr-4 font-mono-av">{fmt(p.likes)}</td>
                    <td className="py-2.5 pr-4 font-mono-av">{p.comments} <span className="text-white/40">({p.unreplied} open)</span></td>
                    <td className="py-2.5 font-mono-av">{p.new_comments_24h}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {summaryState.result && (
            <div className="mt-5 bg-white/5 border-l-4 border-[#9CC4DB] p-4" data-testid="social-summary-result">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#9CC4DB]">
                {summaryState.result.sent ? "Summary email sent" : "Email not sent (SMTP awaiting password) — preview:"}
              </div>
              <pre className="mt-2 text-xs text-white/80 whitespace-pre-wrap font-mono-av leading-relaxed">{summaryState.result.preview}</pre>
            </div>
          )}
        </section>
      )}

      {/* Composer */}
      <Composer accounts={accounts} posts={posts} onPost={post} />

      {/* Comments */}
      <CommentsInbox comments={comments} onReply={replyComments} />
    </div>
  );
}
